import * as React from 'react';
import { useState, useEffect } from 'react';
import ProjectsInfoModal from './ProjectsInfoModal';

interface Project {
  id: number;
  title: string;
  description: string;
  autonomous: boolean;
  status: string;
}

interface MyProjectsProps {
  initialProjects: Project[];
}

function ConfirmationDialog({ isOpen, onConfirm, onCancel, projectTitle }) {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h3>Confirm Deletion</h3>
        <p>Are you sure you want to delete the project "{projectTitle}"?</p>
        <div className="confirmation-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function NoProjectsMessage() {
  return (
    <div className="no-projects-message">
      <i className="material-icons">folder_open</i>
      <h3>No Projects Yet</h3>
      <p>Create a new project to get started!</p>
    </div>
  );
}

interface NewProject {
  title: string;
  description: string;
  autonomous: boolean;
}

function NewProjectModal({ isOpen, onClose, onSave }) {
  const [newProject, setNewProject] = useState<NewProject>({ title: '', description: '', autonomous: true });
  const [error, setError] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setNewProject({ ...newProject, [name]: type === 'checkbox' ? checked : value });
  };

  const resetForm = () => {
    setNewProject({ title: '', description: '', autonomous: true });
    setError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProject.title.trim()) {
      setError('Project name is required');
      return;
    }
    onSave(newProject);
    resetForm();
  };

  // Reset the form when the modal is closed
  React.useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-content">
        <h2>Create New Project</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Project Name*</label>
            <input
              type="text"
              id="title"
              name="title"
              value={newProject.title}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={newProject.description}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="autonomous"
              name="autonomous"
              checked={newProject.autonomous}
              onChange={handleInputChange}
            />
            <label htmlFor="autonomous">
              Autonomous<br />Project
            </label>
          </div>
          {error && <p className="error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create Project</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditProjectModal({ isOpen, onClose, onSave, project }) {
  const [editedProject, setEditedProject] = useState<Project | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (project) {
      setEditedProject(project);
    }
  }, [project]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (editedProject) {
      const { name, value } = e.target;
      setEditedProject({ ...editedProject, [name]: value });
    }
  };

  const handleStatusChange = () => {
    if (editedProject) {
      const newStatus = editedProject.status === 'active' ? 'paused' : 'active';
      setEditedProject({ ...editedProject, status: newStatus });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editedProject) {
      if (!editedProject.autonomous && !editedProject.title.trim()) {
        setError('Project name is required');
        return;
      }
      onSave(editedProject);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !editedProject) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>{editedProject.autonomous ? 'Edit Autonomous Project' : 'Edit Project'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Project Name*</label>
            <input
              type="text"
              id="title"
              name="title"
              value={editedProject.title || ''}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={editedProject.description || ''}
              onChange={handleInputChange}
            />
          </div>
          {editedProject.autonomous && (
            <div className="form-group">
              <button type="button" onClick={handleStatusChange} className="btn btn-secondary">
                {editedProject.status === 'active' ? 'Pause Project' : 'Unpause Project'}
              </button>
            </div>
          )}
          {error && <p className="error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MyProjects({ initialProjects }: MyProjectsProps) {
  const [projects, setProjects] = useState<Project[]>(initialProjects);
  const [showCompleted, setShowCompleted] = useState<boolean>(() =>
    JSON.parse(localStorage.getItem('showCompleted') || 'true')
  );
  const [hidePaused, setHidePaused] = useState<boolean>(() =>
    JSON.parse(localStorage.getItem('hidePaused') || 'false')
  );
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; projectId: number | null; projectTitle: string }>({
    isOpen: false,
    projectId: null,
    projectTitle: '',
  });

  useEffect(() => {
    fetch('/projects')
      .then(response => response.json())
      .then(data => setProjects(data))
      .catch(error => console.error('Error fetching projects:', error));
  }, []);

  // Add effect to persist filter preferences
  useEffect(() => {
    localStorage.setItem('showCompleted', JSON.stringify(showCompleted));
  }, [showCompleted]);

  useEffect(() => {
    localStorage.setItem('hidePaused', JSON.stringify(hidePaused));
  }, [hidePaused]);

  // Filter projects based on preferences
  const filteredProjects = projects.filter(project => {
    if (!showCompleted && project.status === 'completed') return false;
    if (hidePaused && project.status === 'paused') return false;
    return true;
  });

  const handleProjectClick = (id: number) => {
    window.location.href = `/projects/${id}`;
  };

  const handleEdit = (e: React.MouseEvent | React.TouchEvent, project: Project) => {
    e.stopPropagation();
    setEditingProject(project);
  };

  const handleCloseEditModal = () => {
    setEditingProject(null);
  };

  const handleSaveEditedProject = (editedProject: Project) => {
    fetch(`/projects/${editedProject.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(editedProject),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to update project');
        }
        return response.json();
      })
      .then(_data => {
        setProjects(projects.map(p => p.id === editedProject.id ? editedProject : p));
        setEditingProject(null);
      })
      .catch(error => console.error('Error updating project:', error));
  };

  const handleDelete = (e: React.MouseEvent | React.TouchEvent, id: number, title: string) => {
    e.stopPropagation();
    setConfirmDelete({ isOpen: true, projectId: id, projectTitle: title });
  };

  const confirmDeleteProject = () => {
    if (confirmDelete.projectId) {
      fetch(`/projects/${confirmDelete.projectId}`, {
        method: 'DELETE',
      })
        .then(response => {
          if (response.ok) {
            setProjects(projects.filter(project => project.id !== confirmDelete.projectId));
          } else {
            throw new Error('Failed to delete project');
          }
        })
        .then(
          () => {
            // This runs on success
            setConfirmDelete({ isOpen: false, projectId: null, projectTitle: '' });
          },
          error => {
            // This runs on error
            console.error('Error deleting project:', error);
            setConfirmDelete({ isOpen: false, projectId: null, projectTitle: '' });
          }
        );
    }
  };

  const cancelDeleteProject = () => {
    setConfirmDelete({ isOpen: false, projectId: null, projectTitle: '' });
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleProjectClick(id);
    }
  };

  const handleAddNewProject = () => {
    setIsNewProjectModalOpen(true);
  };

  const handleCloseNewProjectModal = () => {
    setIsNewProjectModalOpen(false);
  };

  const handleSaveNewProject = (newProject: Omit<Project, 'id'>) => {
    fetch('/projects', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newProject),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to create project');
        }
        return response.json();
      })
      .then(data => {
        const createdProject: Project = {
          ...data,
          autonomous: newProject.autonomous
        };
        setProjects([createdProject, ...projects]);
        setIsNewProjectModalOpen(false);
      })
      .catch(error => console.error('Error creating project:', error));
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-badge status-active';
      case 'completed':
        return 'status-badge status-completed';
      case 'paused':
        return 'status-badge status-paused';
      default:
        return 'status-badge';
    }
  };

  return (
    <div className="projects-container">
      <style>{`
        .projects-container {
          padding: 20px;
        }
        .page-title {
          font-size: 2.5rem;
          margin-bottom: 20px;
          color: #333;
        }
        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
          margin-top: 30px;
        }
        .project-card {
          background-color: #ffffff;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          padding: 24px;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          cursor: pointer; /* Add this line to change the cursor to a hand */
        }
        .project-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
        }
        .project-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        .project-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #2c3e50;
          margin: 0;
        }
        .project-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
        }
        .badge-status {
          background-color: #e8f5e9;
          color: #2e7d32;
        }
        .badge-status.paused {
          background-color: #fff3e0;
          color: #e65100;
        }
        .badge-status.completed {
          background-color: #e3f2fd;
          color: #1565c0;
        }
        .badge-autonomous {
          background-color: #f3e5f5;
          color: #7b1fa2;
        }
        .project-description {
          color: #7f8c8d;
          font-size: 0.875rem;
          margin-bottom: 16px;
          flex-grow: 1;
        }
        .project-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: auto;
        }
        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s ease;
          cursor: pointer;
          border: none;
        }
        .btn-primary {
          background-color: #3498db;
          color: white;
        }
        .btn-primary:hover {
          background-color: #2980b9;
        }
        .btn-danger {
          background-color: #e74c3c;
          color: white;
        }
        .btn-danger:hover {
          background-color: #c0392b;
        }
        .add-project-btn {
          position: fixed;
          bottom: 30px;
          right: 30px;
          background-color: #2ecc71;
          color: white;
          border-radius: 50px;
          padding: 12px 24px;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        .add-project-btn:hover {
          background-color: #27ae60;
        }
        .material-icons {
          font-size: 24px;
        }
        .confirmation-dialog {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .confirmation-content {
          background-color: white;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        }
        .confirmation-actions {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-top: 20px;
        }
        .no-projects-message {
          text-align: center;
          padding: 40px;
          background-color: #f8f9fa;
          border-radius: 8px;
          color: #6c757d;
        }
        .no-projects-message .material-icons {
          font-size: 48px;
          margin-bottom: 16px;
        }
        .no-projects-message h3 {
          margin-bottom: 8px;
          font-size: 1.5rem;
        }
        .no-projects-message p {
          font-size: 1rem;
        }
        .modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background-color: white;
          padding: 20px;
          border-radius: 8px;
          width: 80%;
          max-width: 500px;
        }
        .form-group {
          margin-bottom: 15px;
        }
        .form-group label {
          display: block;
          margin-bottom: 5px;
        }
        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
        }
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
        }
        .error {
          color: red;
          margin-top: 5px;
        }
        .projects-intro {
          margin-bottom: 30px;
          max-width: 800px;
          line-height: 1.6;
        }
        .project-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .autonomous-badge {
          background-color: #3498db;
          color: white;
          font-size: 0.8rem;
          padding: 2px 6px;
          border-radius: 12px;
          font-weight: bold;
        }
        .project-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 15px;
        }
        .project-actions {
          display: flex;
          gap: 10px;
        }
        .autonomous-badge {
          background-color: white;
          color: #333;
          font-size: 0.8rem;
          padding: 4px 8px;
          border-radius: 12px;
          font-weight: bold;
          border: 1px solid #333;
        }
        .checkbox-group {
          display: flex;
          align-items: flex-start;
          margin-bottom: 15px;
          margin-top: 10px;
        }
        .checkbox-group input[type="checkbox"] {
          margin-right: 10px;
          margin-top: 3px;
        }
        .checkbox-group label {
          cursor: pointer;
          line-height: 1.2;
        }
        .status-badge {
          font-size: 0.8rem;
          padding: 4px 8px;
          border-radius: 12px;
          font-weight: bold;
          text-transform: capitalize;
        }
        .status-active {
          background-color: #2ecc71;
          color: white;
        }
        .status-completed {
          background-color: #3498db;
          color: white;
        }
        .status-paused {
          background-color: #f39c12;
          color: white;
        }
        .project-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .autonomous-note {
          background-color: #f8f9fa;
          border-left: 4px solid #ffc107;
          padding: 10px;
          margin-bottom: 15px;
          font-style: italic;
          color: #6c757d;
        }
        .autonomous-note p {
          margin: 0;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background-color: white;
          padding: 20px;
          border-radius: 8px;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }
        /* Add these new styles for the buttons */
        .project-actions button {
          cursor: pointer; /* Ensure buttons also show the hand cursor */
        }
        .project-actions button:focus {
          outline: 2px solid #3498db; /* Add a focus outline for accessibility */
        }
        .filter-controls {
          display: flex;
          gap: 24px;
          margin-bottom: 24px;
          padding: 16px;
          background: #f8f9fa;
          border-radius: 12px;
        }
        .filter-control {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .filter-control label {
          font-size: 0.95rem;
          color: #4a5568;
          cursor: pointer;
          user-select: none;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        /* Toggle switch styling */
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 48px;
          height: 24px;
        }
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #cbd5e0;
          transition: .4s;
          border-radius: 24px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        input:checked + .toggle-slider {
          background-color: #3498db;
        }
        input:checked + .toggle-slider:before {
          transform: translateX(24px);
        }
        /* Optional: add focus styles */
        input:focus + .toggle-slider {
          box-shadow: 0 0 1px #3498db;
        }
        /* Hover effect */
        .toggle-slider:hover {
          background-color: #a0aec0;
        }
        input:checked + .toggle-slider:hover {
          background-color: #2980b9;
        }
      `}</style>
      <div className="page-header">
        <h2 className="page-title">My Projects</h2>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">My Projects</li>
          </ol>
        </nav>
      </div>
      <div className="projects-intro">
        <p>
          Unlock deeper insights and automate your research with Autonomous AI Agents-powered projects. <ProjectsInfoModal message="Learn how it works" />.
        </p>
      </div>
      <div className="filter-controls">
        <div className="filter-control">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <span>Show Completed</span>
        </div>
        <div className="filter-control">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={hidePaused}
              onChange={(e) => setHidePaused(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <span>Hide Paused</span>
        </div>
      </div>
      {filteredProjects.length > 0 ? (
        <div className="projects-grid">
          {filteredProjects.map((project: Project) => (
            <div
              key={project.id}
              className="project-card"
              onClick={() => handleProjectClick(project.id)}
              onKeyDown={(e) => handleKeyDown(e, project.id)}
              role="button"
              tabIndex={0}
            >
              <div className="project-header">
                <h3 className="project-title">{project.title}</h3>
                <div className="project-badges">
                  <span className={`badge badge-status ${project.status.toLowerCase()}`}>
                    {project.status}
                  </span>
                  {project.autonomous && <span className="badge badge-autonomous">Autonomous</span>}
                </div>
              </div>
              <p className="project-description">
                {project.description ? (
                  project.description.length > 100
                    ? `${project.description.substring(0, 100)}...`
                    : project.description
                ) : 'No description provided'}
              </p>
              <div className="project-actions">
                <button
                  className="btn btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(e, project);
                  }}
                >
                  Edit
                </button>
                <button
                  className="btn btn-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(e, project.id, project.title);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <NoProjectsMessage />
      )}
      <button className="btn add-project-btn" onClick={handleAddNewProject}>
        <i className="material-icons">add</i> Add New Project
      </button>
      <ConfirmationDialog
        isOpen={confirmDelete.isOpen}
        onConfirm={confirmDeleteProject}
        onCancel={cancelDeleteProject}
        projectTitle={confirmDelete.projectTitle}
      />
      <NewProjectModal
        isOpen={isNewProjectModalOpen}
        onClose={handleCloseNewProjectModal}
        onSave={handleSaveNewProject}
      />
      <EditProjectModal
        isOpen={!!editingProject}
        onClose={handleCloseEditModal}
        onSave={handleSaveEditedProject}
        project={editingProject}
      />
    </div>
  );
}

export default MyProjects;
